import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col
} from "reactstrap";
import { toast } from "react-toastify";
import {
  addFollowUpApi,
  updateFollowUpApi
} from "../../../api/apiUrlConst";
import { toWords } from "number-to-words";


interface Props {
  isOpen: boolean;
  toggle: () => void;
  enqFormId: number;
  onSuccess: () => void;
  editData?: any;
}

const AddFollowUpModal: React.FC<Props> = ({
  isOpen,
  toggle,
  enqFormId,
  onSuccess,
  editData
}) => {
  const [form, setForm] = useState<any>({
    interestLevel: "",
    responseStatus: "",
    nextContactDate: "",
    fees: "",
    discountedFees: "",
    message: "",
    call: true,
    whatapp: false,
    email: false
  });

  const isJoined = form.responseStatus === "Joined";

  const showNextContact =
    form.responseStatus === "Yes" ||
    form.responseStatus === "No" ||
    form.responseStatus === "later" ||
    form.responseStatus === "DND";

  // ✅ Prefill when editing
  useEffect(() => {
    if (editData) {
      setForm({
        interestLevel: editData.interestLevel || "",
        responseStatus: editData.responseStatus || "",
        nextContactDate: editData.nextContactDate
          ? editData.nextContactDate.split("T")[0]
          : "",
        fees: editData.fees || "",
        discountedFees: editData.discountedFees || "",
        message: editData.message || "",
        call: editData.call ?? true,
        whatapp: editData.whatapp ?? false,
        email: editData.email ?? false
      });
    } else {
      // Reset form when switching back to Add mode
      setForm({
        interestLevel: "",
        responseStatus: "",
        nextContactDate: "",
        fees: "",
        discountedFees: "",
        message: "",
        call: true,
        whatapp: false,
        email: false
      });
    }
  }, [editData, isOpen]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;

    setForm((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        enqFormId,
        ...form,
        fees: form.fees ? Number(form.fees) : 0,
        discountedFees: form.discountedFees
          ? Number(form.discountedFees)
          : null,
        nextContactDate: form.nextContactDate || null
      };

      if (editData) {
        await updateFollowUpApi(editData.id, payload);
        toast.success("FollowUp updated successfully");
      } else {
        await addFollowUpApi(payload);
        toast.success("FollowUp added successfully");
      }

      toggle();
      onSuccess();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
        error.response?.data ||
        "Operation failed"
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      contentClassName="border-0 shadow-lg"
    >
      {/* ===== HEADER ===== */}
      <ModalHeader
        toggle={toggle}
        style={{
          background: "linear-gradient(90deg, #667eea, #764ba2)",
          color: "#fff",
          fontWeight: 600,
        }}
      >
        {editData ? "✏ Edit FollowUp" : "➕ Add FollowUp"}
      </ModalHeader>

      {/* ===== BODY ===== */}
      <ModalBody style={{ background: "#f8f9fc", padding: "25px" }}>
        <Form className="d-flex flex-column gap-3">

          {/* Interest & Response */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-semibold">Interest Level</Label>
                <Input
                  type="select"
                  name="interestLevel"
                  value={form.interestLevel}
                  onChange={handleChange}
                  className="rounded-pill shadow-sm"
                >
                  <option value="">Select</option>
                  <option value="Hot">🔥 Hot</option>
                  <option value="Warm">🌤 Warm</option>
                  <option value="Cold">❄ Cold</option>
                </Input>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label className="fw-semibold">Response Status</Label>
                <Input
                  type="select"
                  name="responseStatus"
                  value={form.responseStatus}
                  onChange={handleChange}
                  className="rounded-pill shadow-sm"
                >
                  <option value="">Select</option>
                  <option value="Joined">🎉 Joined</option>
                  <option value="Yes">✅ Yes</option>
                  <option value="No">❌ No</option>
                  <option value="later">⏳ Later</option>
                  <option value="DND">🚫 DND</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* Next Contact + Fees */}
          <Row>

            {/* Next Contact Date */}
            {showNextContact && (
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Next Contact Date</Label>
                  <Input
                    type="date"
                    name="nextContactDate"
                    value={form.nextContactDate}
                    onChange={handleChange}
                    className="rounded-pill shadow-sm"
                  />
                </FormGroup>
              </Col>
            )}

            {/* Fees */}
            {isJoined && (
              <Col md={3}>
                <FormGroup>
                  <Label className="fw-semibold">Fees</Label>
                  <Input
                    type="number"
                    name="fees"
                    value={form.fees}
                    onChange={handleChange}
                    className="rounded-pill shadow-sm"
                    placeholder="₹ Amount"
                  />

                  {form.fees && (
                    <div
                      className="mt-2 px-3 py-2"
                      style={{
                        background: "#f1f3f5",
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#495057",
                      }}
                    >
                      {toWords(Number(form.fees))}
                    </div>
                  )}
                </FormGroup>
              </Col>
            )}

            {/* Discount Fees */}
            {isJoined && (
              <Col md={3}>
                <FormGroup>
                  <Label className="fw-semibold">Discounted Fees</Label>
                  <Input
                    type="number"
                    name="discountedFees"
                    value={form.discountedFees}
                    onChange={handleChange}
                    className="rounded-pill shadow-sm"
                    placeholder="₹ Discount"
                  />

                  {form.discountedFees && (
                    <div
                      className="mt-2 px-3 py-2"
                      style={{
                        background: "#f1f3f5",
                        borderRadius: "12px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#495057",
                      }}
                    >
                      {toWords(Number(form.discountedFees))}
                    </div>
                  )}
                </FormGroup>
              </Col>
            )}

          </Row>

          {/* Message */}
          <FormGroup>
            <Label className="fw-semibold">Message</Label>
            <Input
              type="textarea"
              name="message"
              rows={3}
              value={form.message}
              onChange={handleChange}
              className="rounded-3 shadow-sm"
              placeholder="Enter follow up notes..."
            />
          </FormGroup>

          {/* Contact Method Section */}
          <div
            className="p-3"
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)"
            }}
          >
            <Label className="fw-semibold mb-3">
              Contact Method Used
            </Label>

            <Row>
              <Col md={4}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    name="call"
                    checked={form.call}
                    onChange={handleChange}
                  />
                  <Label check className="fw-semibold ms-2">
                    📞 Call
                  </Label>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    name="whatapp"
                    checked={form.whatapp}
                    onChange={handleChange}
                  />
                  <Label check className="fw-semibold ms-2">
                    💬 WhatsApp
                  </Label>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    name="email"
                    checked={form.email}
                    onChange={handleChange}
                  />
                  <Label check className="fw-semibold ms-2">
                    📧 Email
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </div>

        </Form>
      </ModalBody>

      {/* ===== FOOTER ===== */}
      <ModalFooter
        style={{
          background: "#f8f9fc",
          borderTop: "none",
        }}
      >
        <Button
          color="secondary"
          outline
          className="rounded-pill px-4"
          onClick={toggle}
        >
          Cancel
        </Button>

        <Button
          className="rounded-pill px-4"
          style={{
            background: "linear-gradient(90deg, #667eea, #764ba2)",
            border: "none",
            fontWeight: 600,
          }}
          onClick={handleSubmit}
        >
          {editData ? "Update FollowUp" : "Save FollowUp"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddFollowUpModal;